import { useEffect, useMemo, useRef } from "react";

import { useNavigate } from "@tanstack/react-router";
import type { ColumnDef } from "@tanstack/react-table";

import type { components } from "../api/schema";
import {
  canonicalWorkingScopeSearch,
  useResolvedWorkingTeamId,
  useWorkingScope,
  type WorkingScopeSearch,
} from "../lib/workingScope";
import { queryKeys } from "../lib/queryKeys";
import { parseMarkersSearch } from "../lib/routerSearch";
import { useTypedSearch } from "../lib/typedRouter";
import { useListQuery } from "../lib/useListQuery";
import { isShowcaseMode } from "../lib/showcaseMode";
import {
  Badge,
  Button,
  EmptyState,
  FilterBar,
  Page,
  PageHeader,
  type FilterValue,
} from "../ui";
import { AgentMarkers } from "./AgentMarkers";
import {
  MARKER_FACETS,
  markerFacetSearch,
  markerFacetValue,
  markerFiltersToQueryKey,
  markerListFilters,
  type MarkerListQuery,
} from "./markerFacets";
import { PaginatedTableSection } from "./shared/PaginatedTableSection";
import { DateCell } from "./shared/DateCell";
import { NameWithId } from "./shared/NameWithId";
import {
  MarkerDetailDrawer,
  formatEvaluator,
  formatOutput,
  useMarkerDrawerSubject,
  type MarkerDrawerSubject,
  type MarkerListNavigation,
} from "./MarkerDetailDrawer";
import {
  useMarkerRevisionAuthors,
  type MarkerRevisionHistoryResult,
} from "./MarkerRevisionHistory";
import { ShowcaseReadOnlyNotice } from "./ShowcaseReadOnlyNotice";

type MarkerResponse = components["schemas"]["MarkerResponse"];

type MarkersRouteSearch = WorkingScopeSearch & {
  evaluator?: string;
  marker?: string;
  origin?: string;
  output?: string;
};

// Top-level markers List surface. Agent scoped = single-agent configuration
// view; no agent = the working team's list (`team_id`, no client fan-out).
// This container owns the URL state: the `?marker=` drawer subject and the
// /markers/new + /markers/$markerId/edit hand-offs, which preserve ONLY the
// root-owned `agent` param (a stale drawer subject never rides along).
export function Markers() {
  const { agentId } = useWorkingScope();
  const navigate = useNavigate();
  const search = useTypedSearch(parseMarkersSearch);
  const markerId =
    typeof search.marker === "string" && search.marker.length > 0
      ? search.marker
      : undefined;
  const markerFilters = markerListFilters(search);
  const markerFilterValue = markerFacetValue(search);

  function onMarkerFilterChange(next: FilterValue): void {
    void navigate({
      replace: true,
      search: (previous: MarkersRouteSearch) => ({
        ...previous,
        ...markerFacetSearch(next),
      }),
      to: "/markers",
    });
  }

  const navigation = useMemo<MarkerListNavigation>(() => {
    const goToEdit = (id: string, mode: "automate" | "revise") => {
      void navigate({
        params: { markerId: id },
        search: (previous: WorkingScopeSearch) => ({
          ...canonicalWorkingScopeSearch(previous),
          mode,
        }),
        to: "/markers/$markerId/edit",
      });
    };
    return {
      onAutomate: (id) => {
        goToEdit(id, "automate");
      },
      onCloseMarker: () => {
        // Close is a replace so Back does not reopen the drawer.
        void navigate({
          replace: true,
          search: (previous: MarkersRouteSearch) => ({
            ...previous,
            marker: undefined,
          }),
          to: "/markers",
        });
      },
      onCreate: () => {
        void navigate({
          search: canonicalWorkingScopeSearch,
          to: "/markers/new",
        });
      },
      onOpenMarker: (id) => {
        void navigate({
          search: (previous: MarkersRouteSearch) => ({
            ...previous,
            marker: id,
          }),
          to: "/markers",
        });
      },
      onRevise: (id) => {
        goToEdit(id, "revise");
      },
    };
  }, [navigate]);

  // A drawer subject never survives an agent-scope switch (markers.md URL
  // state): any change of scope with the drawer open strips `?marker=`.
  const previousAgentIdRef = useRef(agentId);
  const { onCloseMarker } = navigation;
  useEffect(() => {
    if (previousAgentIdRef.current === agentId) {
      return;
    }
    previousAgentIdRef.current = agentId;
    if (markerId !== undefined) {
      onCloseMarker();
    }
  }, [agentId, markerId, onCloseMarker]);

  if (agentId) {
    return (
      <AgentMarkers
        agentId={agentId}
        filters={markerFilters}
        filterValue={markerFilterValue}
        markerId={markerId}
        navigation={navigation}
        onFilterChange={onMarkerFilterChange}
      />
    );
  }

  return (
    <TeamMarkers
      filters={markerFilters}
      filterValue={markerFilterValue}
      markerId={markerId}
      navigation={navigation}
      onFilterChange={onMarkerFilterChange}
    />
  );
}

type AgentNameById = Record<string, string>;

function markerAgentCell(marker: MarkerResponse, agentNameById: AgentNameById) {
  if (marker.agent_id == null) {
    return <Badge>Built-in</Badge>;
  }

  const name = Object.prototype.hasOwnProperty.call(
    agentNameById,
    marker.agent_id,
  )
    ? agentNameById[marker.agent_id]
    : undefined;

  return <NameWithId id={marker.agent_id} idLabel="Agent ID" name={name} />;
}

// UI-LAW §4 DataTable / FilterBar uniformity: sortable-value columns carry
// accessors; `output`/`evaluator` are the low-cardinality facets. The Agent
// column stays facet-free — the sidebar `?agent=` scope IS the URL filter for
// agent, and a client facet must never duplicate a URL filter.
function teamMarkerColumns(
  agentNameById: AgentNameById,
): Array<ColumnDef<MarkerResponse>> {
  return [
    {
      header: "Name",
      id: "displayName",
      accessorFn: (marker) => marker.current_revision.display_name,
      cell: ({ row }) => (
        <NameWithId
          id={row.original.id}
          idLabel="Marker ID"
          name={row.original.current_revision.display_name}
          strong
        />
      ),
    },
    {
      header: "Version",
      id: "version",
      accessorFn: (marker) => marker.current_revision.revision,
      cell: ({ row }) => String(row.original.current_revision.revision),
    },
    {
      header: "Output",
      id: "output",
      accessorFn: (marker) => formatOutput(marker.current_revision.output),
      cell: ({ row }) => (
        <Badge>{formatOutput(row.original.current_revision.output)}</Badge>
      ),
    },
    {
      header: "Evaluator",
      id: "evaluator",
      accessorFn: (marker) =>
        formatEvaluator(marker.current_revision.evaluator),
      cell: ({ row }) => (
        <Badge>
          {formatEvaluator(row.original.current_revision.evaluator)}
        </Badge>
      ),
    },
    {
      header: "Agent",
      cell: ({ row }) => markerAgentCell(row.original, agentNameById),
    },
    {
      header: "Updated",
      accessorKey: "updated_at",
      cell: ({ row }) => <DateCell value={row.original.updated_at} />,
    },
  ];
}

export interface TeamMarkersViewProps {
  agentNameById: AgentNameById;
  drawer: MarkerDrawerSubject;
  errorMessage: string | null;
  filterValue?: FilterValue;
  hasNextPage: boolean;
  isFetchingNextPage: boolean;
  isPending: boolean;
  items: MarkerResponse[];
  listStateKey: string;
  history: MarkerRevisionHistoryResult;
  navigation: MarkerListNavigation;
  onFilterChange?: (next: FilterValue) => void;
  onFetchNextPage: () => void;
}

export function TeamMarkersView({
  agentNameById,
  drawer,
  errorMessage,
  filterValue = {},
  hasNextPage,
  isFetchingNextPage,
  isPending,
  items,
  listStateKey,
  history,
  navigation,
  onFilterChange = () => undefined,
  onFetchNextPage,
}: TeamMarkersViewProps) {
  const columns = teamMarkerColumns(agentNameById);

  return (
    <Page variant="workspace">
      <PageHeader
        actions={
          isShowcaseMode ? (
            <ShowcaseReadOnlyNotice />
          ) : (
            <Button size="sm" onClick={navigation.onCreate}>
              Create marker
            </Button>
          )
        }
        leading={
          <FilterBar
            facets={MARKER_FACETS}
            onChange={onFilterChange}
            value={filterValue}
          />
        }
      />
      <PaginatedTableSection
        ariaLabel="Markers table"
        columns={columns}
        emptyState={<EmptyState title="No markers" />}
        enableSorting
        errorMessage={errorMessage ?? undefined}
        getRowId={(marker) => marker.id}
        hasNextPage={hasNextPage}
        isFetchingNextPage={isFetchingNextPage}
        isPending={isPending}
        items={items}
        listStateKey={listStateKey}
        onLoadMore={onFetchNextPage}
        onRowClick={(row) => {
          navigation.onOpenMarker(row.original.id);
        }}
        skeletonLabel="Loading markers"
        skeletonRows={5}
        virtual={{ fill: true, interactiveRows: true }}
      />
      <MarkerDetailDrawer
        agentNameById={agentNameById}
        drawer={drawer}
        history={history}
        onAutomate={navigation.onAutomate}
        onDismiss={navigation.onCloseMarker}
        onRevise={navigation.onRevise}
      />
    </Page>
  );
}

interface TeamMarkersProps {
  filters: MarkerListQuery;
  filterValue: FilterValue;
  markerId?: string;
  navigation: MarkerListNavigation;
  onFilterChange: (next: FilterValue) => void;
}

function TeamMarkers({
  filters,
  filterValue,
  markerId,
  navigation,
  onFilterChange,
}: TeamMarkersProps) {
  // Agent label map for the "Agent" column. Reads the canonical `/v1/agents`
  // list through the shared keyset hook so it shares ONE cache entry with the
  // shell selector and the palette (a hand-rolled `useQuery` here stored an
  // incompatible `Record` shape under the same key and crashed those infinite
  // readers).
  const agents = useListQuery("/v1/agents", {
    queryKey: queryKeys.agents.list(),
  });
  const agentNameById = useMemo<AgentNameById>(() => {
    const byId: AgentNameById = {};
    for (const agent of agents.items) {
      byId[agent.id] = agent.display_name;
    }
    return byId;
  }, [agents.items]);

  // TeamMarkers only mounts under All agents (agent scope renders
  // AgentMarkers), so the list is always the working team's markers
  // (docs/09 §4); held until the shell resolves the team.
  const workingTeamId = useResolvedWorkingTeamId();
  const markers = useListQuery("/v1/markers", {
    enabled: workingTeamId !== undefined,
    params: { team_id: workingTeamId, ...filters },
    queryKey: queryKeys.markers.list({
      team_id: workingTeamId,
      ...markerFiltersToQueryKey(filters),
    }),
  });
  const drawer = useMarkerDrawerSubject(markerId, markers.items);
  const history = useListQuery("/v1/markers/{id}/revisions", {
    enabled: markerId !== undefined && drawer.subject?.builtin_key == null,
    pathParams: { id: markerId ?? "" },
    queryKey: queryKeys.markerRevisions.list({ marker_id: markerId }),
  });
  const resolvedHistory = useMarkerRevisionAuthors(history);

  return (
    <TeamMarkersView
      agentNameById={agentNameById}
      drawer={drawer}
      errorMessage={markers.errorMessage ?? null}
      filterValue={filterValue}
      hasNextPage={markers.hasNextPage}
      isFetchingNextPage={markers.isFetchingNextPage}
      isPending={markers.isPending}
      items={markers.items}
      listStateKey={markers.listStateKey}
      history={resolvedHistory}
      navigation={navigation}
      onFilterChange={onFilterChange}
      onFetchNextPage={markers.fetchNextPage}
    />
  );
}
